import { AppConfig } from '@/shared/components/global-nav/app-config';
import { ErrorPage, theme } from '@idu-nightlife/ui-kit';
import { useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { useChildBridge } from './use-child-bridge';

interface LandingPageProps {
  app: AppConfig;
}

/**
 * Hosts one embedded application. All messaging/URL-sync behaviour lives in
 * useChildBridge (specs/016-global-nav-venue-sync); this component is layout only.
 */
export function LandingPage({ app }: LandingPageProps) {
  const { iframeRef, iframeSrc } = useChildBridge(app);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
    if (!link || !app.url) return;

    try {
      link.href = `${new URL(app.url).origin}/favicon.ico`;
    } catch {
      // Invalid app URL — keep the default favicon.
      link.href = '/favicon.ico';
    }
  }, [app.url]);

  if (!iframeSrc) {
    return (
      <ErrorPage
        message={`${app.name} is unavailable`}
        details={`${app.name} isn't set up in this environment. Choose another app or venue from the menu.`}
      />
    );
  }

  return (
    <div
      style={{
        height: `calc(100vh - var(--app-header-height, ${isMobile ? 45 : 60}px))`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <iframe
        ref={iframeRef}
        title={`${app.name} portal`}
        src={iframeSrc}
        style={{ border: 0, width: '100%', height: '100%' }}
        allowFullScreen
        allow="camera; clipboard-write"
      />
    </div>
  );
}
